import type { Resource } from '@crown/framework/data';
import { raw, safeHtml } from '@crown/framework/ir';
import { Alert } from '@crown/ui/alert';
import { Card } from '@crown/ui/card';
import { EmptyState } from '@crown/ui/empty-state';
import { initDiagramZoom } from '../diagrams/diagram-zoom';
import { renderRailroadDiagrams } from '../diagrams/railroad';
import type { ActionLink } from '../page-elements';
import { ActionRow, PillRow } from '../page-elements.tsrx';
import type { DocPageData } from './doc-model';

function artefactLinks(data: DocPageData): ActionLink[] {
  return [
    ...data.entry.assets.map((asset) => ({ label: asset.label, href: asset.href, newTab: asset.newTab })),
    { label: 'Raw markdown', href: `/content/${data.entry.contentPath}`, newTab: true },
  ];
}

/** Once the rendered markdown is in place, draws its railroad diagrams and adds zoom to its figures. */
function enhanceArticle(article: Element): void {
  renderRailroadDiagrams(article);
  initDiagramZoom(article);
}

function Related({ data }: { readonly data: DocPageData }) @{
  <div class="docs-related-list">
    @for (const related of data.relatedDocs; key related.routePath) {
      <a href={`/${related.routePath}`} data-route-link="true" class="docs-related-link">
        <strong>{related.title}</strong>
        <span>{related.summary}</span>
      </a>
    }
  </div>
}

function Doc({ data }: { readonly data: DocPageData }) @{
  // The markdown renderer escapes all text and attribute values it writes, so its output is markup we produced.
  const article = raw(safeHtml(data.html));
  <>
    <section class="docs-hero docs-doc-hero">
      <span class="docs-eyebrow">{data.entry.eyebrow}</span>
      <h1>{data.entry.title}</h1>
      <p class="docs-hero-copy">{data.entry.summary}</p>
      <div class="docs-doc-meta"><PillRow tags={data.entry.tags} /></div>
      <ActionRow actions={artefactLinks(data)} />
    </section>
    <div class="docs-content-layout">
      <article class="docs-article" ref={enhanceArticle}>{article}</article>
      <aside class="docs-page-aside">
        {Card({
          title: 'Open artefacts',
          content: <p class="docs-card-copy">Jump directly to the generated diagrams, flow views, or raw markdown that back this page.</p>,
          footer: <ActionRow actions={artefactLinks(data)} />,
          extraClasses: ['docs-card', 'docs-side-card'],
        })}
        {Card({ title: 'Related docs', content: <Related data={data} />, extraClasses: ['docs-card', 'docs-side-card'] })}
      </aside>
    </div>
  </>
}

export default function DocPageView({ doc }: { readonly doc: Resource<DocPageData> }) @{
  const ready = (): DocPageData | null => {
    const state = doc.get();
    return state.state === 'ready' ? state.value : null;
  };
  <div class="docs-page">
    @if (doc.get().state === 'pending') {
      <section class="docs-section">{EmptyState({ title: 'Loading docs page…', variant: 'compact' })}</section>
    } @else if (doc.get().state === 'error') {
      <section class="docs-section">
        {Alert({ variant: 'danger', title: 'Failed to load page', message: (() => { const state = doc.get(); return state.state === 'error' ? state.error : ''; })() })}
      </section>
    } @else if (ready()) {
      <Doc data={ready() as DocPageData} />
    }
  </div>
}
